import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Json } from "@/integrations/supabase/types";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { 
  Users, 
  Plus, 
  Loader2, 
  Shield, 
  UserCheck, 
  UserX,
  Trash2,
  Edit3,
  Check,
  X
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface Admin {
  id: string;
  user_id: string;
  role_id: string;
  is_active: boolean;
  created_at: string;
  user_email?: string;
  user_name?: string;
  role_name?: string;
  role_display_name?: string;
}

interface AdminRole {
  id: string;
  name: string;
  display_name: string;
  permissions: Json;
  created_at: string;
}

export default function AdminManagement() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  // Add Admin Dialog
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [searchEmail, setSearchEmail] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [searchResults, setSearchResults] = useState<{id: string; email: string; name: string | null}[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);

  // Edit Role Dialog
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<Admin | null>(null);
  const [editRoleId, setEditRoleId] = useState("");
  const [editLoading, setEditLoading] = useState(false);

  // Fetch admins and roles
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch roles
      const { data: rolesData, error: rolesError } = await supabase
        .from('admin_roles')
        .select('*')
        .order('name');
      
      if (rolesError) throw rolesError;
      
      // Custom sort: super_admin first, then marketing, then finance, then support
      const roleOrder: Record<string, number> = {
        'super_admin': 0,
        'marketing': 1,
        'finance': 2,
        'support': 3
      };
      
      const sortedRoles = (rolesData || []).sort((a, b) => {
        const orderA = roleOrder[a.name] ?? 99;
        const orderB = roleOrder[b.name] ?? 99;
        return orderA - orderB;
      });

      setRoles(sortedRoles);

      // Fetch admins with user and role info
      const { data: adminsData, error: adminsError } = await supabase
        .from('admins')
        .select('*')
        .order('created_at', { ascending: false });

      if (adminsError) throw adminsError;

      // Get user and role details
      if (adminsData && adminsData.length > 0) {
        const userIds = adminsData.map(a => a.user_id);
        const roleIds = adminsData.map(a => a.role_id);

        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, email, name')
          .in('id', userIds);

        const profileMap = new Map<string, {email: string | null, name: string | null}>(
          profiles?.map(p => [p.id, { email: p.email, name: p.name }]) || []
        );
        const roleMap = new Map<string, AdminRole>(rolesData?.map(r => [r.id, r as unknown as AdminRole]) || []);

        const enrichedAdmins = adminsData.map(a => ({
          ...a,
          user_email: profileMap.get(a.user_id)?.email,
          user_name: profileMap.get(a.user_id)?.name,
          role_name: roleMap.get(a.role_id)?.name,
          role_display_name: roleMap.get(a.role_id)?.display_name,
        }));

        setAdmins(enrichedAdmins);
      } else {
        setAdmins([]);
      }
    } catch (error: unknown) {
      console.error('Error fetching data:', error);
      toast.error(t('admin.management.toast.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  // Search users by email
  const handleSearchUser = async () => {
    if (!searchEmail.trim()) return;
    
    setSearchLoading(true);
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, name')
        .ilike('email', `%${searchEmail}%`)
        .limit(10);

      if (error) throw error;
      
      // Filter out existing admins
      const existingUserIds = new Set(admins.map(a => a.user_id));
      const filtered = data?.filter(u => !existingUserIds.has(u.id)) || [];
      setSearchResults(filtered);
    } catch (error: unknown) {
      console.error('Error searching users:', error);
      toast.error(t('admin.management.toast.searchError'));
    } finally {
      setSearchLoading(false);
    }
  };

  // Add new admin
  const handleAddAdmin = async () => {
    if (!selectedUserId || !selectedRoleId) {
      toast.error(t('admin.management.toast.selectRequired'));
      return;
    }

    setAddLoading(true);
    try {
      const { error } = await supabase
        .from('admins')
        .insert({
          user_id: selectedUserId,
          role_id: selectedRoleId,
          is_active: true,
        });

      if (error) throw error;

      toast.success(t('admin.management.toast.addSuccess'));
      setIsAddOpen(false);
      resetAddForm();
      setRefreshKey(k => k + 1);
    } catch (error: unknown) {
      console.error('Error adding admin:', error);
      const message = error instanceof Error ? error.message : undefined;
      toast.error(message || t('admin.management.toast.addError'));
    } finally {
      setAddLoading(false);
    }
  };

  const resetAddForm = () => {
    setSearchEmail("");
    setSelectedUserId("");
    setSelectedRoleId("");
    setSearchResults([]);
  };

  // Toggle admin active status
  const handleToggleActive = async (admin: Admin) => {
    // 防止停用自己
    if (admin.user_id === user?.id) {
      toast.error(t('admin.management.toast.cantDeactivateSelf'));
      return;
    }

    // 如果要停用的是 super_admin，檢查是否為最後一個
    if (admin.is_active && admin.role_name === 'super_admin') {
      const activeSuperAdmins = admins.filter(
        a => a.role_name === 'super_admin' && a.is_active
      );
      if (activeSuperAdmins.length <= 1) {
        toast.error(t('admin.management.toast.cantDeactivateLastSuper'));
        return;
      }
    }

    try {
      const { error } = await supabase
        .from('admins')
        .update({ is_active: !admin.is_active, updated_at: new Date().toISOString() })
        .eq('id', admin.id);

      if (error) throw error;

      toast.success(admin.is_active ? t('admin.management.toast.deactivated') : t('admin.management.toast.activated'));
      setRefreshKey(k => k + 1);
    } catch (error: unknown) {
      console.error('Error toggling admin status:', error);
      toast.error(t('admin.management.toast.toggleError'));
    }
  };

  // Delete admin
  const handleDeleteAdmin = async (admin: Admin) => {
    // 防止刪除自己
    if (admin.user_id === user?.id) {
      toast.error(t('admin.management.toast.cantDeleteSelf'));
      return;
    }

    // 如果要刪除的是 super_admin，檢查是否為最後一個
    if (admin.role_name === 'super_admin') {
      const activeSuperAdmins = admins.filter(
        a => a.role_name === 'super_admin' && a.is_active
      );
      if (activeSuperAdmins.length <= 1 && admin.is_active) {
        toast.error(t('admin.management.toast.cantDeleteLastSuper'));
        return;
      }
    }

    if (!confirm(t('admin.management.actions.confirmDelete', { email: admin.user_email }))) return;

    try {
      const { error } = await supabase
        .from('admins')
        .delete()
        .eq('id', admin.id);

      if (error) throw error;

      toast.success(t('admin.management.toast.deleted'));
      setRefreshKey(k => k + 1);
    } catch (error: unknown) {
      console.error('Error deleting admin:', error);
      toast.error(t('admin.management.toast.deleteError'));
    }
  };

  // Open edit role dialog
  const openEditRole = (admin: Admin) => {
    setEditingAdmin(admin);
    setEditRoleId(admin.role_id);
    setIsEditOpen(true);
  };

  // Save role change
  const handleSaveRole = async () => {
    if (!editingAdmin || !editRoleId) return;

    setEditLoading(true);
    try {
      const { error } = await supabase
        .from('admins')
        .update({ role_id: editRoleId, updated_at: new Date().toISOString() })
        .eq('id', editingAdmin.id);

      if (error) throw error;

      toast.success(t('admin.management.toast.roleUpdated'));
      setIsEditOpen(false);
      setEditingAdmin(null);
      setRefreshKey(k => k + 1);
    } catch (error: unknown) {
      console.error('Error updating role:', error);
      toast.error(t('admin.management.toast.updateError'));
    } finally {
      setEditLoading(false);
    }
  };

  // Role badge color
  const getRoleBadgeColor = (roleName?: string) => {
    switch (roleName) {
      case 'super_admin': return 'bg-[#FF6B9D] text-white';
      case 'support': return 'bg-[#6FEDD6] text-black';
      case 'marketing': return 'bg-[#FFD93D] text-black';
      case 'finance': return 'bg-[#4DA6FF] text-white';
      default: return 'bg-gray-200 text-gray-700';
    }
  };

  const getRoleLabel = (roleName?: string) => {
    if (!roleName) return "";
    return t(`admin.management.roles.${roleName}`);
  };

  // Translate permission code
  const translatePermission = (perm: string): string => {
    return t(`admin.management.permissions.${perm}`);
  };

  if (loading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-8 py-8">
      {/* Page Header */}
      <div className="mb-8 flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-4xl font-black tracking-tighter text-[#1A1A1A]">{t('admin.management.title')}</h2>
          <p className="text-lg font-medium text-gray-500">{t('admin.management.subtitle')}</p>
        </div>
        <button
          onClick={() => setIsAddOpen(true)}
          className="flex items-center gap-2 rounded-xl border-2 border-black bg-[#6FEDD6] px-5 py-3 font-bold shadow-[4px_4px_0_#000] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[2px_2px_0_#000] transition-all"
        >
          <Plus className="h-5 w-5" />
          {t('admin.management.addAdmin')}
        </button>
      </div>

      {/* Admin List */}
      <div className="rounded-2xl border-2 border-black bg-white shadow-[4px_4px_0_#000] overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-gray-100 border-b-2 border-black">
            <tr>
              <th className="px-6 py-4 font-bold uppercase tracking-wider">{t('admin.management.table.admin')}</th>
              <th className="px-6 py-4 font-bold uppercase tracking-wider">{t('admin.management.table.role')}</th>
              <th className="px-6 py-4 font-bold uppercase tracking-wider">{t('admin.management.table.status')}</th>
              <th className="px-6 py-4 font-bold uppercase tracking-wider">{t('admin.management.table.joinedAt')}</th>
              <th className="px-6 py-4 font-bold uppercase tracking-wider text-right">{t('admin.management.table.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {admins.length === 0 ? (
              <tr>
                <td colSpan={5} className="py-12 text-center text-gray-500 font-medium">
                  <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                  {t('admin.management.table.empty')}
                </td>
              </tr>
            ) : (
              admins.map((admin) => (
                <tr key={admin.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-black bg-[#FFD93D] font-bold">
                        {(admin.user_name || admin.user_email || "?").charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-bold">{admin.user_name || t('admin.management.table.unnamed')}</div>
                        <div className="text-xs text-gray-500">{admin.user_email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border-2 border-black px-3 py-1 text-xs font-bold",
                      getRoleBadgeColor(admin.role_name)
                    )}>
                      <Shield className="h-3.5 w-3.5" />
                      {getRoleLabel(admin.role_name)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {admin.is_active ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full border-2 border-black bg-[#6FEDD6] px-3 py-1 text-xs font-bold">
                        <UserCheck className="h-3.5 w-3.5" />
                        {t('admin.management.status.active')}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-gray-100 px-3 py-1 text-xs font-bold text-gray-500">
                        <UserX className="h-3.5 w-3.5" />
                        {t('admin.management.status.inactive')}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {new Date(admin.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEditRole(admin)}
                        className="rounded-lg border-2 border-black bg-white p-2 hover:bg-gray-100 transition-colors"
                        title={t('admin.management.actions.changeRole')}
                      >
                        <Edit3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(admin)}
                        className={cn(
                          "rounded-lg border-2 border-black p-2 transition-colors",
                          admin.is_active ? "bg-gray-100 hover:bg-gray-200" : "bg-[#6FEDD6] hover:bg-[#5ed9c2]"
                        )}
                        title={admin.is_active ? t('admin.management.actions.deactivate') : t('admin.management.actions.activate')}
                      >
                        {admin.is_active ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                      </button>
                      <button
                        onClick={() => handleDeleteAdmin(admin)}
                        className="rounded-lg border-2 border-black bg-red-100 p-2 hover:bg-red-200 text-red-600 transition-colors"
                        title={t('admin.management.actions.delete')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Roles Reference */}
      <div className="mt-8 rounded-2xl border-2 border-black bg-white p-6 shadow-[4px_4px_0_#000]">
        <h3 className="mb-4 text-xl font-black">{t('admin.management.roles.title')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {roles.map((role) => (
            <div key={role.id} className="rounded-xl border-2 border-black p-4 bg-gray-50">
              <span className={cn(
                "flex items-center justify-center gap-2 rounded-full border-2 border-black px-4 py-1.5 text-sm font-bold mb-3 w-[180px] mx-auto",
                getRoleBadgeColor(role.name)
              )}>
                <Shield className="h-4 w-4" />
                {getRoleLabel(role.name)}
              </span>
              <div className="text-sm text-gray-600 space-y-1.5 w-fit mx-auto">
                {Array.isArray(role.permissions) && role.permissions.map((perm: string, i: number) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <Check className="h-4 w-4 text-green-600" />
                    {translatePermission(perm)}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Admin Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="sm:max-w-lg border-2 border-black shadow-[8px_8px_0_#1A1A1A] bg-[#F5F0E8] gap-6">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">{t('admin.management.dialog.add.title')}</DialogTitle>
            <DialogDescription className="text-base font-medium text-gray-600">
              {t('admin.management.dialog.add.description')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Search User */}
            <div className="space-y-3">
              <Label className="text-base font-black uppercase tracking-wider">{t('admin.management.dialog.add.searchLabel')}</Label>
              <div className="flex gap-3">
                <input
                  type="email"
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  placeholder={t('admin.management.dialog.add.searchPlaceholder')}
                  className="flex-1 rounded-xl border-2 border-black px-4 py-2.5 font-bold text-base focus:shadow-[4px_4px_0_#000] focus:outline-none transition-shadow"
                />
                <button
                  onClick={handleSearchUser}
                  disabled={searchLoading}
                  className="rounded-xl border-2 border-black bg-white px-5 py-2.5 font-black text-base hover:bg-gray-100 hover:shadow-[2px_2px_0_#000] hover:translate-x-[-1px] hover:translate-y-[-1px] active:shadow-none active:translate-x-[1px] active:translate-y-[1px] transition-all whitespace-nowrap"
                >
                  {searchLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('admin.management.dialog.add.searchButton')}
                </button>
              </div>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
                <Label className="font-bold">{t('admin.management.dialog.add.selectUser')}</Label>
                <div className="max-h-40 overflow-y-auto space-y-2 rounded-xl border-2 border-black p-3 bg-white shadow-[4px_4px_0_#000]">
                  {searchResults.map((user) => (
                    <button
                      key={user.id}
                      onClick={() => setSelectedUserId(user.id)}
                      className={cn(
                        "w-full text-left rounded-lg p-3 transition-colors border-2",
                        selectedUserId === user.id
                          ? "bg-[#6FEDD6] border-black shadow-[2px_2px_0_#000]"
                          : "border-transparent hover:bg-gray-100"
                      )}
                    >
                      <div className="font-bold text-lg">{user.name || user.email}</div>
                      <div className="text-sm text-gray-500 font-medium">{user.email}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Role Selection */}
            <div className="space-y-3">
              <Label className="text-base font-black uppercase tracking-wider">{t('admin.management.dialog.add.selectRole')}</Label>
              <div className="grid grid-cols-2 gap-3">
                {roles.map((role) => (
                  <button
                    key={role.id}
                    onClick={() => setSelectedRoleId(role.id)}
                    className={cn(
                      "rounded-xl border-2 border-black px-4 py-3 text-base font-black transition-all text-black",
                      getRoleBadgeColor(role.name),
                      selectedRoleId === role.id
                        ? "shadow-[4px_4px_0_#000] translate-x-[-2px] translate-y-[-2px] opacity-100"
                        : "opacity-50 hover:opacity-100 hover:shadow-[2px_2px_0_#000] hover:translate-x-[-1px] hover:translate-y-[-1px]"
                    )}
                  >
                    {getRoleLabel(role.name)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => { setIsAddOpen(false); resetAddForm(); }}
              className="px-6 py-2.5 rounded-xl border-2 border-black bg-white font-bold hover:bg-gray-100 transition-colors"
            >
              {t('admin.management.dialog.add.cancel')}
            </button>
            <button
              onClick={handleAddAdmin}
              disabled={addLoading || !selectedUserId || !selectedRoleId}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl border-2 border-black bg-[#6FEDD6] font-bold shadow-[2px_2px_0_#000] hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-[4px_4px_0_#000] active:shadow-none active:translate-x-[2px] active:translate-y-[2px] transition-all disabled:opacity-50 disabled:shadow-none disabled:translate-x-0 disabled:translate-y-0"
            >
              {addLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('admin.management.dialog.add.submit')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-md border-2 border-black shadow-[8px_8px_0_#1A1A1A] bg-[#F5F0E8]">
          <DialogHeader>
            <DialogTitle className="text-xl font-black">{t('admin.management.dialog.edit.title')}</DialogTitle>
            <DialogDescription>
              {t('admin.management.dialog.edit.description', { email: editingAdmin?.user_email })}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-2">
              {roles.map((role) => (
                <button
                  key={role.id}
                  onClick={() => setEditRoleId(role.id)}
                  className={cn(
                    "rounded-lg border-2 border-black px-3 py-3 text-sm font-bold transition-colors",
                    editRoleId === role.id
                      ? getRoleBadgeColor(role.name)
                      : "bg-white hover:bg-gray-100"
                  )}
                >
                  {getRoleLabel(role.name)}
                </button>
              ))}
            </div>
          </div>

          <DialogFooter className="flex justify-end gap-3">
            <button
              onClick={() => setIsEditOpen(false)}
              className="px-4 py-2 rounded-lg border-2 border-black bg-white font-bold hover:bg-gray-100 transition-colors"
            >
              {t('admin.management.dialog.edit.cancel')}
            </button>
            <button
              onClick={handleSaveRole}
              disabled={editLoading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-black bg-[#6FEDD6] font-bold shadow-[2px_2px_0_#000] hover:translate-x-0.5 hover:translate-y-0.5 hover:shadow-[1px_1px_0_#000] active:shadow-none transition-all disabled:opacity-50"
            >
              {editLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('admin.management.dialog.edit.submit')}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
